import { useCallback, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { Field, Form as FinalForm } from 'react-final-form';
import { useHistory } from 'react-router-dom';
import classNames from 'classnames';
import jwtDecode from 'jwt-decode';

import { FormProvider } from '@guestyci/foundation/enums';
import FormField from '@guestyci/foundation/FormField';
import Form from '@guestyci/foundation/Form';
import EmailInput from '@guestyci/foundation/EmailInput';
import { email } from '@guestyci/foundation/validators';
import createStyles from '@guestyci/foundation/createStyles';
import TextField from '@guestyci/foundation/TextField';
import { Col } from '@guestyci/foundation/Layout';
import RaisedButton from '@guestyci/foundation/RaisedButton';
import theme from '@guestyci/foundation/theme/defaultTheme';
import useMediaQuery from '@guestyci/foundation/useMediaQuery/useMediaQuery';

import config from 'config';

import {
  setGoogleSignUp as setGoogleSignUpAction,
  setRegularSignUp as setRegularSignUpAction,
} from 'app/store/app-state.slice';
import * as appRoutes from 'app/routes';
import _extends from '@babel/runtime/helpers/esm/extends';
import AppStyle from 'app/Styles';
import { TERMS_AND_CONDITIONS_LINK, PRIVACY_POLICY_LINK } from 'app/constants/constants';
import GoogleSignUpButton from 'app/components/GoogleSignUpButton';
import useQueryParams from 'app/hooks/useQueryParams';
import { FIELD_NAME } from 'app/constants/form-fields';
import track from 'app/utils/dio/registration-page';

const useClasses = createStyles(() =>
  _extends(AppStyle, {
    root: {
      maxWidth: theme.breakpoints.values.sm,
      margin: '0 auto',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
    },
    horizontalLine: {
      width: '100%',
      maxWidth: 400,
      height: '1px',
      backgroundColor: '#0F265C',
      position: 'relative',
      margin: '1.8rem 0',

      '&::after': {
        fontSize: '1.25rem',
        content: 'attr(data-ui--text)',
        backgroundColor: '#FAFAFA',
        position: 'absolute',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        padding: '0.875rem',
        lineHeight: 1,
      },
    },
    agreementText: {
      textAlign: 'center',
      padding: '0 20px',
      maxWidth: 430,
    },
    formWrap: {
      maxWidth: 400,
      width: '100%',
    },
  }));

const mediaQuery = {
  maxWidth: 'md',
};

function ChooseSignupPage() {
  const classes = useClasses();
  const dispatch = useDispatch();
  const history = useHistory();
  const queryParams = useQueryParams();
  const isMobile = useMediaQuery(mediaQuery);
  const showGoogleBtn = queryParams.get('showGoogle');
  const queryEmail = queryParams.get('email') || '';

  const destination = `${appRoutes.FORM}${history.location.search}`;

  const handleSignUpGoogle = useCallback(
    (response) => {
      dispatch(setGoogleSignUpAction(response.credential));
      history.push(destination);
      const googleEmail = jwtDecode(response.credential).email;

      track.firstPageSignUpGoogleCompleted(googleEmail);
    },
    [destination, dispatch, history]
  );

  const handleRegularSignUp = useCallback(
    (formValues) => {
      dispatch(setRegularSignUpAction(formValues.email));
      track.firstPageFillData(formValues.email);
      history.push(destination);
    },
    [destination, dispatch, history]
  );

  useEffect(() => {
    if (queryEmail) {
      dispatch(setRegularSignUpAction(queryEmail));
      track.firstPageFillData(queryEmail);
      history.push(destination);
    }
  }, []);

  return (
    <div className={classes.root} id="sign-up-page-container">
      <Col align="center" spacing={3} className="mb-12">
        <TextField className={classes.pageHeading}>Create your account</TextField>

        <TextField className="font-size-xl">
          <span id="sign-up-already-hosting" className="pr-1">
            Already hosting with us?
          </span>
          <a
            rel="noreferrer"
            id="sign-up-sign-in-button"
            href={config.applicationHost}
            className={classNames(classes.darkLink, 'underline', 'font-weight-bold')}
          >
            Sign in
          </a>
        </TextField>
      </Col>

      <FinalForm
        onSubmit={handleRegularSignUp}
        initialValues={{
          [FIELD_NAME.EMAIL]: queryParams.get('email') || '',
        }}
        render={({ handleSubmit }) => (
          <Form
            react-final-form="true"
            onSubmit={handleSubmit}
            provider={FormProvider.Final}
            fieldInstance={Field}
            className={classes.formWrap}
          >
            <Col spacing={4} id="sign-up-email-input">
              <FormField name={FIELD_NAME.EMAIL} validate={[email]} required>
                <EmailInput
                  placeholder="Enter your work email address"
                  withActions={false}
                  autoComplete="username"
                  className={classes.marketingFormInput}
                />
              </FormField>

              <RaisedButton
                className={[classes.marketingSuccessButton, 'sign-up-button', 'w-full']}
                type="submit"
                id="sign-up-next-button"
              >
                NEXT
              </RaisedButton>
            </Col>
          </Form>
        )}
      />

      {showGoogleBtn && (
        <>
          <div className={classes.horizontalLine} data-ui--text="or" />

          <GoogleSignUpButton width={isMobile ? 0 : 400} onSignup={handleSignUpGoogle} />
        </>
      )}

      <TextField className={classes.agreementText} id="sign-up-agreement-text">
        <span className="pr-1">
          Signing up for a Guesty for Hosts account means you agree to the
        </span>
        <a
          target="_blank"
          rel="noreferrer"
          id="sign-up-agreement-privacy-policy-button"
          href={PRIVACY_POLICY_LINK}
        >
          Privacy Policy
        </a>
        <span className="px-1">and</span>
        <a
          target="_blank"
          rel="noreferrer"
          id="sign-up-agreement-terms-button"
          href={TERMS_AND_CONDITIONS_LINK}
        >
          Terms and Conditions
        </a>
      </TextField>
    </div>
  );
}

export default ChooseSignupPage;
